import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { ExportDataService } from '../../../shared/services/export-data.service';
import { DatePipe } from '@angular/common';
import { ColDef, GridApi, GridReadyEvent, createGrid } from 'ag-grid-community';

interface ParentChildMetricRule {
  key: string;
  aggregate: 'sum' | 'count';
  distinct: boolean;
  sourceField?: string;
  distinctKey?: string;
  conditionField?: string;
  conditionValues: string[];
}

interface ParentRow {
  id: string;
  expanded: boolean;
  groupValues: Record<string, string>;
  childRows: Array<Record<string, any>>;
  summaryRow: Record<string, any>;
}

@Component({
  selector: 'app-parent-child-table',
  templateUrl: './parent-child-table.component.html',
  styleUrl: './parent-child-table.component.css',
  standalone: false
})
export class ParentChildTableComponent implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @Input() Temp: boolean = false;

  loading = false;
  matrixLoader = false;
  isExporting = false;
  showMatrix = false;
  allExpanded = false;
  currentDate: Date = new Date();
  currentTime = '';
  groupByColumns: string[] = [];
  childColumns: string[] = [];
  parentRows: ParentRow[] = [];
  parentGridData: any[] = [];
  parentColDefs: ColDef[] = [];
  childColDefs: ColDef[] = [];
  appNameColumns: string[] = [];
  primaryAppColumn = 'Apps';

  readonly defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 130
  };

  readonly getChildRowClass = (params: any): string =>
    params?.data?._isSummary ? 'pct-summary-row-class' : '';

  readonly getParentRowHeight = (params: any): number => {
    if (params?.data?._rowType !== 'detail') {
      return 38;
    }

    const parentId = String(params?.data?._parentId ?? '');
    const measuredHeight = this.detailRowHeights.get(parentId);
    if (measuredHeight && measuredHeight > 0) {
      return measuredHeight;
    }

    const detailRows = Array.isArray(params?.data?._detailRows) ? params.data._detailRows.length : 0;
    const headerHeight = 38;
    const rowHeight = 34;

    // Conservative fallback before measured child grid height is available.
    return Math.max(76, headerHeight + (detailRows * rowHeight) + 16);
  };

  readonly getParentRowClass = (params: any): string =>
    params?.data?._rowType === 'detail' ? 'pct-detail-row' : '';

  private rawRows: any[] = [];
  private metricRules: ParentChildMetricRule[] = [];
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private parentGridApi?: GridApi;
  private childGridInstances = new Map<string, any>();
  private childDebugLoggedIds = new Set<string>();
  private detailRowHeights = new Map<string, number>();

  private readonly refreshCancel$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private readonly norm = (v: string) => (v ?? '').toLowerCase().replace(/[\s_\-]+/g, '');

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private inbuiltUtilityService: InbuiltUtilityService,
    private exportDataService: ExportDataService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.updateGmtTimestamp();
    this.selectedFilters = this.config?.selectedFilters || {};
    this.subscribeToFilterEvents();
    this.showMatrix = true;
    this.resetAndFetch();
  }

  ngOnDestroy(): void {
    this.destroyChildGrids();
    this.refreshCancel$.next();
    this.refreshCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleParentRow(row: ParentRow): void {
    row.expanded = !row.expanded;

    this.refreshParentGridData();

    this.allExpanded = this.parentRows.length > 0 && this.parentRows.every((parentRow: ParentRow) => parentRow.expanded);
  }

  downloadToExcel(): void {
    if (!this.rawRows || this.rawRows.length === 0) {
      this.toastr.error('No data available to download.', 'Download', { timeOut: 3000, positionClass: 'toast-top-center' });
      return;
    }

    this.isExporting = true;
    const reportName = this.config?.reportNameFromQueryParams || this.elementData?.name || 'Report';
    try {
      this.exportDataService.exportToExcel(reportName, [this.rawRows]);
    } finally {
      this.isExporting = false;
    }
  }

  toggleExpandCollapseAll(): void {
    const expand = !this.allExpanded;
    this.parentRows.forEach((row: ParentRow) => {
      row.expanded = expand;
    });

    this.refreshParentGridData();

    this.allExpanded = expand;
  }

  onGridReady(params: GridReadyEvent): void {
    this.parentGridApi = params.api;
    this.parentGridApi.setGridOption('rowData', this.parentGridData);
  }

  onParentCellClicked(event: any): void {
    if (event?.colDef?.field !== 'actions') {
      return;
    }

    if (!event?.data?._parentId || event?.data?._rowType !== 'parent') {
      return;
    }

    const parentId = String(event?.data?._parentId ?? '');
    const row = this.parentRows.find((item: ParentRow) => item.id === parentId);
    if (!row) {
      return;
    }

    this.toggleParentRow(row);
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        this.showMatrix = true;
        this.selectedFilters = event?.selectedFilters || {};
        this.inputSearchFilter = event?.inputSearchFilter || '';
        this.resetAndFetch();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.elementData || !this.config) {
      return false;
    }

    const evReport = String(event?.reportNameFromQueryParams ?? '');
    const evPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const evTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (evReport !== myReport || evPage !== myPage || evTab !== myTab) {
      return false;
    }

    if (typeof event?.showTable === 'boolean') {
      return true;
    }

    const filterName = String(event?.filterName ?? '');
    if (!filterName) {
      return false;
    }

    const rawDeps = this.elementData?.filterList;
    if (!rawDeps) {
      return true;
    }

    let deps: any[] = [];
    if (Array.isArray(rawDeps)) {
      deps = rawDeps;
    } else if (typeof rawDeps === 'string') {
      try {
        deps = JSON.parse(rawDeps);
      } catch {
        deps = [rawDeps];
      }
    }

    if (deps.length === 0) {
      return true;
    }

    return deps.some((dep: any) => {
      const k = dep && typeof dep === 'object' ? String(Object.keys(dep)[0] ?? '') : String(dep ?? '');
      const v = dep && typeof dep === 'object' ? String(Object.values(dep)[0] ?? '') : String(dep ?? '');
      return k === filterName || v === filterName || String(dep ?? '') === filterName;
    });
  }

  private resetAndFetch(): void {
    this.refreshCancel$.next();
    this.loading = true;
    this.matrixLoader = true;
    this.updateGmtTimestamp();
    this.rawRows = [];
    this.groupByColumns = [];
    this.childColumns = [];
    this.parentRows = [];
    this.parentGridData = [];
    this.destroyChildGrids();
    this.childDebugLoggedIds.clear();
    this.detailRowHeights.clear();
    this.allExpanded = false;
    this.metricRules = [];
    this.getParentChildTableData();
  }

  private getParentChildTableData(): void {
    let payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage?.key || '',
      tab: this.config?.currentTab?.key || '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || '',
      download: false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      inputSearchFilter: this.inputSearchFilter
    };
    if (!this.Temp) {
      payload = this.inbuiltUtilityService.getTablePayload(this.selectedFilters);
    }
    this.reportsService.getInbuildTableData(payload, this.Temp)
      .pipe(takeUntil(this.refreshCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows: any[] = res?.data || [];
          this.rawRows = [...rows];
          this.applyResponseConfig(res?.groupByColumns, res?.columnInfo, rows);
          this.buildParentColDefs();
          this.buildChildColDefs();
          this.parentRows = this.buildParentRows(rows);
          this.refreshParentGridData();
          this.allExpanded = false;
          this.loading = false;
          this.matrixLoader = false;
        },
        error: (err: any) => {
          this.loading = false;
          this.matrixLoader = false;
          this.handleErrors(err);
        }
      });
  }

  private applyResponseConfig(groupByColumns: any, columnInfo: any, rows: any[]): void {
    const sampleRow = Array.isArray(rows) && rows.length > 0 ? rows[0] : {};
    const allKeys = Object.keys(sampleRow || {});

    const responseGroupBy = Array.isArray(groupByColumns)
      ? groupByColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean)
      : [];

    const configuredGroupBy = this.resolveGroupByColumns();
    const preferredGroupBy = responseGroupBy.length > 0 ? responseGroupBy : configuredGroupBy;

    this.groupByColumns = [...new Set(preferredGroupBy)];

    const groupSet = new Set(this.groupByColumns.map((key: string) => this.norm(key)));
    this.childColumns = allKeys.filter((key: string) => !groupSet.has(this.norm(key)));
    this.appNameColumns = this.resolveAppNameColumns(allKeys);
    this.primaryAppColumn = this.appNameColumns[0] || this.primaryAppColumn;

    this.metricRules = this.buildMetricRules(columnInfo, allKeys, this.groupByColumns);

    if (this.metricRules.length === 0 && this.childColumns.length > 0) {
      this.metricRules = this.childColumns.map((key: string) => ({
        key,
        aggregate: 'sum',
        distinct: false,
        conditionValues: []
      }));
    }
  }

  private buildMetricRules(columnInfo: any, allKeys: string[], groupColumns: string[]): ParentChildMetricRule[] {
    if (!columnInfo || typeof columnInfo !== 'object' || Array.isArray(columnInfo)) {
      return [];
    }

    const groupSet = new Set(groupColumns.map((key: string) => this.norm(key)));
    const rules: ParentChildMetricRule[] = [];

    for (const [rawKey, rawDefinition] of Object.entries(columnInfo)) {
      const resolvedKey = allKeys.find((key: string) => this.norm(key) === this.norm(String(rawKey))) || String(rawKey);
      if (!resolvedKey || groupSet.has(this.norm(resolvedKey))) {
        continue;
      }

      const parsedRule = this.parseMetricDefinition(rawDefinition, resolvedKey, allKeys);
      if (!parsedRule) {
        continue;
      }

      rules.push(parsedRule);
    }

    return rules;
  }

  private parseMetricDefinition(definition: any, resolvedKey: string, allKeys: string[]): ParentChildMetricRule | null {
    if (typeof definition === 'string') {
      const aggregate = this.normalizeAggregate(definition);
      return aggregate
        ? { key: resolvedKey, aggregate, distinct: false, conditionValues: [] }
        : null;
    }

    if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
      return null;
    }

    const aggregate = this.normalizeAggregate(
      definition.aggregate
      ?? definition.aggregation
      ?? definition.type
      ?? definition.metric
      ?? definition.value
    );

    if (!aggregate) {
      return null;
    }

    const distinct = !!definition.distinct;
    const sourceFieldCandidate = definition.sourceField ?? definition.sourceKey ?? definition.source;
    const sourceField = sourceFieldCandidate
      ? this.resolveColumnName(allKeys, String(sourceFieldCandidate))
      : undefined;

    const distinctKeyCandidate = definition.distinctKey ?? definition.distinctOn ?? definition.key;
    const distinctKey = distinctKeyCandidate
      ? this.resolveColumnName(allKeys, String(distinctKeyCandidate))
      : (sourceField || resolvedKey);

    const parsedCondition = this.parseMetricCondition(definition.condition, allKeys, resolvedKey);

    return {
      key: resolvedKey,
      aggregate,
      distinct,
      sourceField,
      distinctKey,
      conditionField: parsedCondition.field,
      conditionValues: parsedCondition.values
    };
  }

  private normalizeAggregate(value: any): 'sum' | 'count' | null {
    const aggregate = String(value ?? '').trim().toLowerCase();
    if (aggregate === 'sum') {
      return 'sum';
    }
    if (aggregate === 'count') {
      return 'count';
    }
    return null;
  }

  private parseMetricCondition(rawCondition: any, allKeys: string[], fallbackField: string): { field?: string; values: string[] } {
    if (!rawCondition) {
      return { field: undefined, values: [] };
    }

    if (typeof rawCondition === 'object' && !Array.isArray(rawCondition)) {
      const fieldCandidate = rawCondition.field ?? rawCondition.key ?? rawCondition.column;
      const field = fieldCandidate ? this.resolveColumnName(allKeys, String(fieldCandidate)) : undefined;
      const valuesSource = rawCondition.values ?? rawCondition.value ?? [];
      const values = Array.isArray(valuesSource)
        ? valuesSource.map((v: any) => String(v ?? '').trim().toLowerCase()).filter(Boolean)
        : [String(valuesSource ?? '').trim().toLowerCase()].filter(Boolean);

      return { field, values };
    }

    const conditionText = String(rawCondition ?? '').trim();
    if (!conditionText) {
      return { field: undefined, values: [] };
    }

    const clauses = conditionText
      .split(/\s+(?:AND|OR)\s+/i)
      .map((part: string) => part.trim())
      .filter(Boolean);

    const values: string[] = [];
    let field: string | undefined;

    for (const clause of clauses) {
      const eqIndex = clause.indexOf('==') >= 0 ? clause.indexOf('==') : clause.indexOf('=');
      if (eqIndex < 0) {
        values.push(String(clause ?? '').trim().toLowerCase());
        continue;
      }

      const opLen = clause.indexOf('==') >= 0 ? 2 : 1;
      const leftSide = clause.slice(0, eqIndex).trim();
      const rightSide = clause.slice(eqIndex + opLen).trim();

      if (!field && leftSide) {
        field = this.resolveColumnName(allKeys, leftSide || fallbackField);
      }

      const cleaned = rightSide.replace(/[()"']/g, '').trim();
      values.push(...cleaned.split(',').map((item: string) => item.trim().toLowerCase()).filter(Boolean));
    }

    return {
      field: field || this.resolveColumnName(allKeys, fallbackField),
      values: [...new Set(values.filter(Boolean))]
    };
  }

  private buildParentRows(rows: any[]): ParentRow[] {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const groups = new Map<string, { groupValues: Record<string, string>; rows: any[] }>();

    for (const row of rows) {
      const groupValues: Record<string, string> = {};

      if (this.groupByColumns.length === 0) {
        groupValues['Group'] = 'All';
      } else {
        for (const groupCol of this.groupByColumns) {
          const value = this.resolveRowValue(row, groupCol) || 'N/A';
          groupValues[groupCol] = value;
        }
      }

      const keyParts = Object.values(groupValues).map((value: string) => String(value ?? '').trim());
      const groupKey = keyParts.join('||');

      const existing = groups.get(groupKey);
      if (!existing) {
        groups.set(groupKey, { groupValues, rows: [row] });
      } else {
        existing.rows.push(row);
      }
    }

    return Array.from(groups.entries()).map(([id, grouped]) => {
      const childRows = this.buildDistinctAppRows(grouped.rows);
      const summaryRow = this.buildSummaryRow(grouped.rows);

      return {
        id,
        expanded: false,
        groupValues: grouped.groupValues,
        childRows,
        summaryRow
      };
    });
  }

  private refreshParentGridData(): void {
    this.destroyChildGrids();
    this.detailRowHeights.clear();

    const flattenedRows: any[] = [];
    for (const row of this.parentRows) {
      flattenedRows.push({
        ...row.groupValues,
        _rowType: 'parent',
        _parentId: row.id,
        _expanded: row.expanded
      });

      if (row.expanded) {
        flattenedRows.push({
          _rowType: 'detail',
          _parentId: row.id,
          _detailRows: this.buildChildGridData(row)
        });
      }
    }

    this.parentGridData = flattenedRows;

    if (this.parentGridApi) {
      this.parentGridApi.setGridOption('rowData', this.parentGridData);
      this.parentGridApi.refreshCells({ force: true, columns: ['actions'] });
    }
  }

  private buildChildGridData(row: ParentRow): any[] {
    const rawRows = (row.childRows || []).map((child: Record<string, any>) => ({ ...child, _isSummary: false }));
    const filteredRows = rawRows.filter((child: Record<string, any>) => !this.isChildRowEmpty(child));

    if (!this.childDebugLoggedIds.has(row.id)) {
      this.childDebugLoggedIds.add(row.id);
      const emptyRows = rawRows.filter((child: Record<string, any>) => this.isChildRowEmpty(child));
      console.groupCollapsed(`[ParentChildTable Debug] parent=${row.id}`);
      console.log('raw child rows:', rawRows.length);
      console.log('filtered child rows:', filteredRows.length);
      console.log('empty child rows removed:', emptyRows.length);
      console.log('sample empty child row:', emptyRows[0] || null);
      console.log('sample filtered child row:', filteredRows[0] || null);
      console.groupEnd();
    }

    return filteredRows;
  }

  private isChildRowEmpty(row: Record<string, any>): boolean {
    return !this.childColumns.some((col: string) => this.isChildCellValuePresent(row?.[col]));
  }

  private isChildCellValuePresent(value: any): boolean {
    if (value === 0) {
      return true;
    }

    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized !== '' && normalized !== 'null' && normalized !== 'undefined';
  }

  private buildParentColDefs(): void {
    const groupCols = this.groupByColumns.length > 0 ? this.groupByColumns : ['Group'];
    this.parentColDefs = [
      ...groupCols.map((col: string, index: number) => ({
        headerName: col,
        field: col,
        flex: 1,
        minWidth: 150,
        colSpan: (params: any) => params?.data?._rowType === 'detail' && index === 0
          ? groupCols.length + 1
          : 1,
        valueGetter: (params: any) => {
          if (params?.data?._rowType === 'detail') {
            return index === 0 ? '__detail__' : '';
          }
          return params?.data?.[col] ?? '';
        },
        cellStyle: (params: any) => {
          if (params?.data?._rowType === 'detail') {
            return index === 0
              ? { padding: '0', borderRight: 'none', backgroundColor: '#f4f8fd', display: 'block', fontWeight: 400 }
              : { padding: '0', borderRight: 'none', backgroundColor: '#f4f8fd', display: 'none', fontWeight: 400 };
          }
          return { fontWeight: 600 };
        },
        cellRenderer: (params: any) => {
          if (params?.data?._rowType === 'detail') {
            if (index !== 0) {
              return '';
            }

            const parentId = String(params?.data?._parentId ?? '');
            return this.renderChildGridContainer(parentId);
          }

          if (index === 0) {
            return `<span style="font-weight:600;">${String(params?.value ?? '')}</span>`;
          }

          return String(params?.value ?? '');
        }
      } as ColDef)),
      {
        headerName: 'Actions',
        field: 'actions',
        headerClass: 'pct-actions-header',
        sortable: false,
        filter: false,
        resizable: false,
        width: 100,
        cellStyle: (params: any) => {
          if (params?.data?._rowType === 'detail') {
            return { textAlign: 'center', display: 'none' };
          }
          return { textAlign: 'center' };
        },
        cellRenderer: (params: any) => {
          if (params?.data?._rowType !== 'parent') {
            return '';
          }

          const icon = params?.data?._expanded ? 'remove_circle_outline' : 'add_circle_outline';
          return `<span class="material-icons pct-toggle-icon" style="cursor:pointer;color:#013b71;font-size:20px;line-height:1;">${icon}</span>`;
        }
      } as ColDef
    ];
  }

  private buildChildColDefs(): void {
    this.childColDefs = this.childColumns.map((col: string) => ({
      headerName: col,
      field: col,
      flex: 1,
      minWidth: 130
    } as ColDef));
  }

  private renderChildGridContainer(parentId: string): string {
    const gridId = `pct-child-grid-${this.escapeId(parentId)}`;

    setTimeout(() => {
      const host = document.getElementById(gridId);
      if (!host) {
        return;
      }

      // Recreate the child grid for rerendered detail rows (for example after grid filtering/reset).
      const existingApi = this.childGridInstances.get(gridId);
      if (existingApi) {
        try {
          existingApi.destroy?.();
        } catch {
          // no-op
        }
        this.childGridInstances.delete(gridId);
      }

      const parent = this.parentRows.find((row: ParentRow) => row.id === parentId);
      const detailRows = parent ? this.buildChildGridData(parent) : [];

      const api = createGrid(host, {
        columnDefs: this.childColDefs,
        rowData: detailRows,
        defaultColDef: this.defaultColDef,
        rowHeight: 34,
        headerHeight: 38,
        domLayout: 'autoHeight',
        suppressMovableColumns: true,
        suppressHorizontalScroll: true,
        getRowClass: this.getChildRowClass,
        theme: 'legacy'
      });

      requestAnimationFrame(() => {
        const measured = Math.ceil(host.parentElement?.getBoundingClientRect?.().height || host.getBoundingClientRect().height);
        if (measured > 0) {
          this.applyMeasuredDetailHeight(parentId, measured);
        }
      });

      requestAnimationFrame(() => {
        const rowCount = api?.getDisplayedRowCount?.() ?? -1;
        const hostHeight = host.getBoundingClientRect().height;
        const wrapHeight = host.parentElement?.getBoundingClientRect?.().height ?? -1;
        console.log('[ParentChildTable Debug] child grid rendered', {
          parentId,
          inputRows: detailRows.length,
          displayedRows: rowCount,
          hostHeight,
          wrapperHeight: wrapHeight
        });
      });

      this.childGridInstances.set(gridId, api);
    }, 0);

    return `<div class="pct-inline-child-wrap"><div id="${gridId}" class="ag-theme-alpine pct-inline-child-grid"></div></div>`;
  }

  private destroyChildGrids(): void {
    this.childGridInstances.forEach((api: any) => {
      try {
        api?.destroy?.();
      } catch {
        // no-op
      }
    });
    this.childGridInstances.clear();
  }

  private escapeId(value: string): string {
    return String(value ?? '').replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  private applyMeasuredDetailHeight(parentId: string, height: number): void {
    this.detailRowHeights.set(parentId, height);
    if (!this.parentGridApi) {
      return;
    }

    this.parentGridApi.forEachNode((node: any) => {
      if (node?.data?._rowType === 'detail' && String(node?.data?._parentId ?? '') === parentId) {
        node.setRowHeight(height);
      }
    });

    this.parentGridApi.onRowHeightChanged();
  }

  private buildDistinctAppRows(rows: any[]): Array<Record<string, any>> {
    const appBuckets = new Map<string, any[]>();

    for (const row of rows || []) {
      const appName = this.resolveAppDisplayName(row) || 'N/A';
      const bucketKey = this.norm(appName) || `app-${appBuckets.size}`;
      const existing = appBuckets.get(bucketKey) || [];
      existing.push(row);
      appBuckets.set(bucketKey, existing);
    }

    const outputRows: Array<Record<string, any>> = [];
    appBuckets.forEach((bucketRows: any[], appKey: string) => {
      const firstRow = bucketRows[0] || {};
      const output: Record<string, any> = {};

      for (const col of this.childColumns) {
        output[col] = this.computeBucketColumnValue(col, bucketRows, firstRow, appKey);
      }

      outputRows.push(output);
    });

    return outputRows.sort((left: Record<string, any>, right: Record<string, any>) => {
      const a = String(left[this.primaryAppColumn] ?? '');
      const b = String(right[this.primaryAppColumn] ?? '');
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }

  private computeBucketColumnValue(col: string, bucketRows: any[], firstRow: any, appKey: string): any {
    if (this.norm(col) === this.norm(this.primaryAppColumn)) {
      return this.resolveAppDisplayName(firstRow) || appKey;
    }

    const metricRule = this.metricRules.find((rule: ParentChildMetricRule) => this.norm(rule.key) === this.norm(col));

    if (metricRule?.aggregate === 'sum') {
      return this.formatCompactCurrency(this.sumBucketColumn(bucketRows, col));
    }

    if (metricRule?.aggregate === 'count') {
      return this.countBucketColumn(bucketRows, metricRule, col);
    }

    return this.resolveRowValue(firstRow, col);
  }

  private sumBucketColumn(bucketRows: any[], col: string): number {
    let total = 0;
    for (const row of bucketRows) {
      const parsed = this.parseNumeric(this.resolveRowValue(row, col));
      if (Number.isFinite(parsed)) {
        total += parsed;
      }
    }
    return total;
  }

  private countBucketColumn(bucketRows: any[], rule: ParentChildMetricRule, col: string): number {
    let count = 0;
    const distinctSet = new Set<string>();

    for (const row of bucketRows) {
      if (!this.passesCondition(rule, row)) {
        continue;
      }

      if (rule.distinct) {
        count += this.countDistinctRuleValue(rule, row, distinctSet);
        continue;
      }

      const rawValue = this.resolveRowValue(row, col);
      if (!rawValue) {
        continue;
      }

      const numeric = this.parseNumeric(rawValue);
      count += Number.isFinite(numeric) ? numeric : 1;
    }

    return count;
  }

  private buildSummaryRow(rows: any[]): Record<string, any> {
    const summary: Record<string, any> = {};

    for (const col of this.childColumns) {
      summary[col] = '';
    }

    if (this.childColumns.some((col: string) => this.norm(col) === this.norm(this.primaryAppColumn))) {
      summary[this.primaryAppColumn] = 'Total';
    }

    for (const rule of this.metricRules) {
      if (!this.childColumns.some((col: string) => this.norm(col) === this.norm(rule.key))) {
        continue;
      }

      if (rule.aggregate === 'sum') {
        summary[rule.key] = this.formatCompactCurrency(this.sumRuleValues(rows, rule));
        continue;
      }

      if (rule.aggregate === 'count') {
        summary[rule.key] = this.countRuleValues(rows, rule);
      }
    }

    return summary;
  }

  private sumRuleValues(rows: any[], rule: ParentChildMetricRule): number {
    let total = 0;
    for (const row of rows) {
      const parsed = this.parseNumeric(this.resolveRowValue(row, rule.key));
      if (Number.isFinite(parsed)) {
        total += parsed;
      }
    }
    return total;
  }

  private countRuleValues(rows: any[], rule: ParentChildMetricRule): number {
    let count = 0;
    const distinctSet = new Set<string>();

    for (const row of rows) {
      if (!this.passesCondition(rule, row)) {
        continue;
      }

      if (rule.distinct) {
        count += this.countDistinctRuleValue(rule, row, distinctSet);
        continue;
      }

      const rawValue = this.resolveRowValue(row, rule.key);
      if (!rawValue) {
        continue;
      }

      const numeric = this.parseNumeric(rawValue);
      count += Number.isFinite(numeric) ? numeric : 1;
    }

    return count;
  }

  private countDistinctRuleValue(rule: ParentChildMetricRule, row: any, distinctSet: Set<string>): number {
    const distinctField = this.resolveDistinctValueKey(rule, row);
    const normalized = this.norm(this.resolveRowValue(row, distinctField));
    if (!normalized || distinctSet.has(normalized)) {
      return 0;
    }

    distinctSet.add(normalized);
    return 1;
  }

  private passesCondition(rule: ParentChildMetricRule, row: any): boolean {
    if (!rule.conditionValues || rule.conditionValues.length === 0) {
      return true;
    }

    const fieldCandidates = [rule.conditionField, rule.key, 'Pending Apps', 'Apps'].filter(Boolean) as string[];
    const conditionField = fieldCandidates.find((candidate: string) => {
      const fieldValueRaw = this.resolveRowValue(row, candidate);
      return String(fieldValueRaw ?? '').trim() !== '';
    }) || rule.conditionField || rule.key;

    const fieldValueRaw = this.resolveRowValue(row, conditionField);
    const fieldValue = String(fieldValueRaw ?? '').trim().toLowerCase();

    if (!fieldValue) {
      return false;
    }

    return rule.conditionValues.map((value: string) => value.toLowerCase()).includes(fieldValue);
  }

  private resolveDistinctValueKey(rule: ParentChildMetricRule, row: any): string {
    const candidates = [rule.sourceField, rule.distinctKey, rule.key, 'Apps'].filter(Boolean) as string[];
    for (const candidate of candidates) {
      const value = this.resolveRowValue(row, candidate);
      if (String(value ?? '').trim() !== '') {
        return candidate;
      }
    }

    return rule.sourceField || rule.distinctKey || rule.key || 'Apps';
  }

  private resolveGroupByColumns(): string[] {
    const configGroupByColumns = this.elementData?.config?.groupByColumns ?? this.config?.groupByColumns;

    if (Array.isArray(configGroupByColumns)) {
      return configGroupByColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean);
    }

    if (typeof configGroupByColumns === 'string') {
      return configGroupByColumns.split(',').map((item: string) => item.trim()).filter(Boolean);
    }

    const legacyGroupColumns = this.elementData?.config?.groupColumns ?? this.config?.groupColumns;
    if (Array.isArray(legacyGroupColumns)) {
      return legacyGroupColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean);
    }

    if (typeof legacyGroupColumns === 'string') {
      return legacyGroupColumns.split(':').map((item: string) => item.trim()).filter(Boolean);
    }

    return [];
  }

  private resolveAppNameColumns(allKeys: string[]): string[] {
    const appNameCols = this.elementData?.config?.appNameColumns ?? this.config?.appNameColumns;
    if (Array.isArray(appNameCols)) {
      return appNameCols.map((c: any) => String(c ?? '').trim()).filter(Boolean);
    }

    if (typeof appNameCols === 'string') {
      return appNameCols.split(',').map((c: string) => c.trim()).filter(Boolean);
    }

    const preferredAppColumns = (allKeys || [])
      .map((key: string) => ({ key, score: this.getAppColumnScore(key) }))
      .filter((candidate: { key: string; score: number }) => candidate.score > 0)
      .sort((left: { key: string; score: number }, right: { key: string; score: number }) => right.score - left.score)
      .map((candidate: { key: string; score: number }) => candidate.key);

    if (preferredAppColumns.length > 0) {
      return [preferredAppColumns[0]];
    }

    return ['Apps'];
  }

  private getAppColumnScore(key: string): number {
    const normalizedKey = this.norm(key);

    if (normalizedKey === 'apps') {
      return 110;
    }

    if (normalizedKey === 'appname' || normalizedKey === 'applicationname' || normalizedKey === 'app_name' || normalizedKey === 'longappname') {
      return 100;
    }

    if (normalizedKey.includes('applicationname') || normalizedKey.includes('appname')) {
      return 90;
    }

    if (normalizedKey === 'application' || normalizedKey === 'app') {
      return 80;
    }

    if (normalizedKey.includes('application') || normalizedKey.includes('app')) {
      return 70;
    }

    return 0;
  }

  private resolveAppDisplayName(row: any): string {
    for (const col of this.appNameColumns) {
      const value = this.resolveRowValue(row, col);
      if (value) {
        return value;
      }
    }

    const fallback = this.resolveRowValue(row, this.primaryAppColumn);
    return fallback || '';
  }

  private resolveColumnName(allKeys: string[], candidate: string): string {
    const normalizedCandidate = this.norm(candidate);
    return allKeys.find((key: string) => this.norm(key) === normalizedCandidate) || candidate;
  }

  private resolveRowValue(row: any, key: string): string {
    const normalizedKey = this.norm(key);

    for (const col of Object.keys(row || {})) {
      if (this.norm(col) === normalizedKey) {
        return String(row[col] ?? '').trim();
      }
    }

    return '';
  }

  private parseNumeric(value: string): number {
    const normalized = String(value ?? '')
      .trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/\$/g, '')
      .replace(/,/g, '');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  private formatSumValue(value: string): string {
    const parsed = this.parseNumeric(value);
    if (!Number.isFinite(parsed)) {
      return value;
    }

    return this.formatCompactCurrency(parsed);
  }

  private formatCompactCurrency(value: number): string {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1_000_000_000) {
      return `${sign}$${(absValue / 1_000_000_000).toFixed(2)}B`;
    }

    if (absValue >= 1_000_000) {
      return `${sign}$${(absValue / 1_000_000).toFixed(2)}M`;
    }

    if (absValue >= 1_000) {
      return `${sign}$${(absValue / 1_000).toFixed(2)}K`;
    }

    return `${sign}$${absValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  private updateGmtTimestamp(): void {
    const now = new Date();
    this.currentDate = now;
    this.currentTime = `${this.datePipe.transform(now, 'hh:mm a', 'GMT') || ''} GMT`;
  }

  private handleErrors(err: any): void {
    console.error('Parent/child table error:', err);
    this.toastr.error('An error occurred while fetching table data. Please try again later.', 'Error', {
      timeOut: 5000,
      positionClass: 'toast-top-center'
    });
  }
}
